import { FC, useEffect, useRef, useState } from "react"
import { handleAuthorizedRequest, trends } from "../../../../apis"
import Card from "../components/Card"
import { KDropdown, KSpan } from "kahuna-base-react-components"
import {
  formatNumber,
  getPercentage,
  mapToCamelCase,
  processPlatformOptions,
  toYYYYMMDD
} from "../../../../utility"
import { TrendTrack, User } from "../../../../types"
//@ts-ignore
import HeadphonesIcon from "../../../../assets/analytics_icons/headphones-black.svg"
//@ts-ignore
import RecordLabelIcon from "../../../../assets/analytics_icons/record-label-black.svg"
//@ts-ignore
import MusicAlbum from "../../../../assets/analytics_icons/music-album-black.svg"
//@ts-ignore
import DownloadIcon from "../../../../assets/analytics_icons/download.svg"
//@ts-ignore
import PlayIcon from "../../../../assets/analytics_icons/play-circle.svg"
//@ts-ignore
import ViewIcon from "../../../../assets/analytics_icons/eye.svg"
//@ts-ignore
import VideoReelsIcon from "../../../../assets/analytics_icons/video-reels.svg"
import { lang } from "../../../../constants/languages"
import PlatformShareBar from "./PlatformShareBar"
import { RootState } from "../../../../store"
import { connect } from "react-redux"
import TrendChart from "./TrendChart"
import { ANALYSIS_TAB_OPTIONS, DAY, TREND_TRACK_EVENT_TYPE } from "../../../../constants"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { DateRangeType } from "kahuna-base-react-components/dist/components/KSelectRangeDate/KSelectRangeDate"
import Loading from "../../../ui/Loading"

export type TotalEvents = {
  all: number
  download: number
  dsp: number
  socialProduction: number
  socialConsumption: number
}
export interface ArtistOverviewData {
  trendTrackList?: TrendTrack[]
  platformStreamMap: { [key: string]: number }
  activeTrackNumber: number
  activeReleaseNumber: number
  selectedPeriodTotalEvents: TotalEvents
  prevPeriodTotalEvents: TotalEvents
  availableDateList: string[]
}

export type ArtistOverviewProps = {
  user: User
  dateValues: DateRangeType
  selectedTab: string | undefined
  selectedUsersParam: string
  isAdmin: boolean
}

export const eventTypeOptions = [
  /*{ label: "All", value: 1, value2: "all" },*/
  { label: "Download", value: 2, value2: TREND_TRACK_EVENT_TYPE.DOWNLOAD },
  { label: "DSP", value: 3, value2: TREND_TRACK_EVENT_TYPE.DSP },
  { label: "Social", value: 4, value2: "social" }
]

const ArtistOverview: FC<ArtistOverviewProps> = (props) => {
  const {dateValues} = props
  const [overviewData, setOverviewData] = useState<ArtistOverviewData>()
  const [selectedEventType, setSelectedEventType] = useState<string>(TREND_TRACK_EVENT_TYPE.DSP)
  const lastFetchedOptionsKeyRef = useRef<string>("")
  const [platformOptions, setPlatformOptions] = useState<KSelectOption[]>([])
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const lastRequestKeyRef = useRef("")

  const fetchArtistOverview = async (eventType: string, startDate: Date, endDate: Date) => {
    const start_date = toYYYYMMDD(startDate)
    const end_date = toYYYYMMDD(endDate)
    let fetch_options = false
    const optionsKey = `${start_date}_${end_date}_${eventType}`
    if (lastFetchedOptionsKeyRef.current !== undefined && lastFetchedOptionsKeyRef.current !== optionsKey) {
      fetch_options = true
      lastFetchedOptionsKeyRef.current = optionsKey
    }
    const platformParam = selectedPlatforms.length > 0 ? `&platform_list=${selectedPlatforms.join(",")}` : ""
    const userIdsParam = props.isAdmin && props.selectedUsersParam ? `&user_ids=${props.selectedUsersParam}` : ""
    const isAdminParam = props.isAdmin ? `&is_admin=1` : ""
    setLoading(true)
    try {
      await handleAuthorizedRequest(async () => {
        const eventTypeParam =
          eventType === "social" ? "social_production,social_consumption" : eventType === "all" ? "" : eventType
        const response = await trends(
          `/artist_overview/?start_date=${start_date}&end_date=${end_date}&event_type=${eventTypeParam}${platformParam}${userIdsParam}${isAdminParam}`,
          "get"
        )
        if (response && response.data) {
          const overview: ArtistOverviewData = mapToCamelCase(response.data)
          setOverviewData(overview)
          if (fetch_options) {
            const platforms = Object.keys(overview?.platformStreamMap)
              ?.map((key) => key)
              ?.sort((a, b) => a.localeCompare(b))
            const processedPlatformOptions = processPlatformOptions(platforms)
            setPlatformOptions(processedPlatformOptions)
          }
        }
      }, "")
    } finally {
      setLoading(false)
    }
  }
  const makeKey = (dates: Date[], platforms: string[], eventType: string, selectedUsersParam: string) => {
    const start = toYYYYMMDD(dates[0])
    const end = toYYYYMMDD(dates[1])

    const pKey = [...platforms].sort().join("|")

    return `${start}_${end}__p:${pKey}__et:${eventType}__su:${selectedUsersParam}`
  }

  useEffect(() => {
    if (props.selectedTab !== ANALYSIS_TAB_OPTIONS.ARTIST_OVERVIEW) return 
  
    const dates = dateValues as Date[]
    if (!(dates[0] && dates[1])) return

    const start_key = toYYYYMMDD(dates[0])
    const end_key = toYYYYMMDD(dates[1])

    const dateKey = `${start_key}_${end_key}`

    const prevDateKey = lastRequestKeyRef.current.split("__p:")[0]

    if (prevDateKey && prevDateKey !== dateKey) {
      // check if the useEffect is triggered by the date change and reset the selected platforms if exist without causing double request.
      if (selectedPlatforms.length > 0) {
        setSelectedPlatforms([])
        return
      }
    }

    const key = makeKey(dates, selectedPlatforms, selectedEventType, props.selectedUsersParam)
    if (key === lastRequestKeyRef.current) return
    lastRequestKeyRef.current = key
    
    const startDate = dates[0]
    const endDate = dates[1]
    const range = endDate.getTime() - startDate.getTime()
    const start = new Date(startDate.getTime() - (range + DAY))
    //const start = new Date(dates[0].getTime() - 7 * DAY) // It is necessary to get the previous week data
    fetchArtistOverview(selectedEventType, start, dates[1])
  }, [selectedEventType, dateValues, selectedPlatforms, props.selectedTab, props.selectedUsersParam])

  const getCardTitle = (selectedEventType: string) => {
    if (selectedEventType === "all") {
      return lang.trends.headers.total_activity
    } else if (selectedEventType === TREND_TRACK_EVENT_TYPE.DOWNLOAD) {
      return lang.trends.headers.total_downloads
    } else if (selectedEventType === TREND_TRACK_EVENT_TYPE.DSP) {
      return lang.trends.headers.total_streams
    } else if (selectedEventType === "social") {
      return lang.trends.headers.videos_created
    }
    return lang.trends.headers.total_activity
  }

  const getChartTitle = (selectedEventType: string) => {
    if (selectedEventType === "all") {
      return lang.trends.headers.total_activity
    } else if (selectedEventType === TREND_TRACK_EVENT_TYPE.DOWNLOAD) {
      return lang.trends.headers.downloads
    } else if (selectedEventType === TREND_TRACK_EVENT_TYPE.DSP) {
      return lang.trends.headers.streams
    } else if (selectedEventType === "social") {
      return lang.trends.headers.videos_created
    }
    return lang.trends.headers.total_activity
  }

  const getIcon = (selectedEventType: string) => {
    if (selectedEventType === "all") {
      return PlayIcon
    } else if (selectedEventType === TREND_TRACK_EVENT_TYPE.DOWNLOAD) {
      return DownloadIcon
    } else if (selectedEventType === TREND_TRACK_EVENT_TYPE.DSP) {
      return HeadphonesIcon
    } else if (selectedEventType === "social") {
      return VideoReelsIcon
    }
    return PlayIcon
  }

  const getValue = (selectedEventType: string, prevWeek?: boolean) => {
    const weekKey = prevWeek ? "prevPeriodTotalEvents" : "selectedPeriodTotalEvents"
    const totalValues = overviewData?.[weekKey]
    const download = totalValues?.download || 0
    const dsp = totalValues?.dsp || 0
    const socialConsumption = totalValues?.socialConsumption || 0
    const socialProduction = totalValues?.socialProduction || 0
    const all = download + dsp + socialConsumption + socialProduction
    if (selectedEventType === "all") {
      return all
    } else if (selectedEventType === TREND_TRACK_EVENT_TYPE.DOWNLOAD) {
      return download
    } else if (selectedEventType === TREND_TRACK_EVENT_TYPE.DSP) {
      return dsp
    } else if (selectedEventType === "social") {
      return socialProduction
    }
    return ""
  }

  const getTooltipParam = (selectedEventType: string) => {
    if (selectedEventType === "all") {
      return lang.trends.headers.all_in_one
    } else if (selectedEventType === TREND_TRACK_EVENT_TYPE.DOWNLOAD) {
      return lang.trends.headers.download
    } else if (selectedEventType === TREND_TRACK_EVENT_TYPE.DSP) {
      return lang.trends.headers.stream
    } else if (selectedEventType === "social") {
      return lang.trends.headers.video_usage
    }
    return ""
  }

  if (loading) return <Loading />

  return (
    <div className="flex flex-col gap-2 h-full">
      <div className="flex flex-row gap-1.5 my-10 items-center justify-between">
        <div className="flex flex-row items-center gap-3">
          <div className="flex flex-col gap-1 justify-center items-start">
            <KSpan text={lang.trends.filter.category} color="#111" fontWeight={500} />
            <KDropdown
              width={140}
              rightIcon="/analytics_icons/caret-down-new.svg"
              enableIndicator={true}
              options={eventTypeOptions}
              onSelect={(selected) => {
                setSelectedEventType((selected as KSelectOption)?.value2 || "")
                setSelectedPlatforms([])
              }}
              selected={eventTypeOptions.find((option) => option.value2 === selectedEventType)}
            />
          </div>
          <div className="flex flex-col gap-1 justify-center items-start">
            <KSpan text={lang.trends.filter.platforms} color="#111" fontWeight={500} />
            <KDropdown
              isClearable={true}
              isMulti={true}
              showOnlyIconsInMulti={true}
              width={200}
              rightIcon="/analytics_icons/caret-down-new.svg"
              enableIndicator={true}
              closeMenuOnSelect={false}
              options={platformOptions}
              onSelect={() => {}}
              onBlur={(selected) => {
                if (!selected) return
                const value2List = (selected as KSelectOption[])?.map((entity) => entity.value2 || "") || []
                const isSame =
                  value2List.length === selectedPlatforms.length &&
                  value2List.every((v, i) => v === selectedPlatforms[i])
                if (!isSame) {
                  setSelectedPlatforms(value2List)
                }
              }}
              placeholder={lang.trends.filter.all}
              selected={platformOptions.filter(
                (option) => option?.value2 && selectedPlatforms.includes(option?.value2)
              )}
            />
          </div>
        </div>
      </div>
      <div className="flex gap-3 w-full max-[1000px]:flex-col">
        <Card
          value={formatNumber(getValue(selectedEventType) || "")}
          title={getCardTitle(selectedEventType)}
          icon={getIcon(selectedEventType)}
          percentChange={getPercentage(getValue(selectedEventType, true) || 0, getValue(selectedEventType) || 0)}
        />
        {selectedEventType === "social" && (
          <Card
            value={formatNumber(overviewData?.selectedPeriodTotalEvents.socialConsumption || "")}
            title={lang.trends.headers.total_video_views}
            icon={ViewIcon}
            percentChange={getPercentage(
              overviewData?.prevPeriodTotalEvents.socialConsumption || 0,
              overviewData?.selectedPeriodTotalEvents.socialConsumption || 0
            )}
          />
        )}
        <Card
          value={`${(overviewData?.activeTrackNumber)?.toLocaleString("en-US") || 0}`}
          title={lang.trends.headers.active_tracks}
          icon={RecordLabelIcon}
          tooltipContent={lang.trends.headers.active_tracks_tooltip.replace(
            "<param>",
            getTooltipParam(selectedEventType)
          )}
        />
        <Card
          value={`${overviewData?.activeReleaseNumber?.toLocaleString("en-US") || 0}`}
          title={lang.trends.headers.active_releases}
          icon={MusicAlbum}
          tooltipContent={lang.trends.headers.active_releases_tooltip.replace(
            "<param>",
            getTooltipParam(selectedEventType)
          )}
        />
      </div>
      <div className="w-full">
        <PlatformShareBar platformStreamMap={overviewData?.platformStreamMap || {}} />
      </div>
      <div className="flex h-[400px] gap-3 max-[1000px]:flex-col">
        <div className="flex min-w-0 flex-1 basis-0 h-full items-center justify-center">
          <TrendChart
            data={
              selectedEventType === "social"
                ? (overviewData?.trendTrackList || [])?.filter((item) => item.eventType === "social_production")
                : overviewData?.trendTrackList || []
            }
            availableDateList = {overviewData?.availableDateList || []}
            dateValues={dateValues}
            title={`${lang.trends.headers.trend_snapshot} (${getChartTitle(selectedEventType)})`}
          />
        </div>
        {selectedEventType === "social" && (
          <div className="flex min-w-0 flex-1 basis-0 h-full items-center justify-center">
            <TrendChart
              data={(overviewData?.trendTrackList || [])?.filter((item) => item.eventType === "social_consumption")}
              availableDateList = {overviewData?.availableDateList  || []}
              dateValues={dateValues}
              title={`${lang.trends.headers.trend_snapshot} (${lang.trends.headers.video_views})`}
              lineColor={"#DB7093"}
            />
          </div>
        )}
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.auth.user
  }
}

export default connect(mapStateToProps)(ArtistOverview)